"use client";

import { Control, Controller } from "react-hook-form";
import { Edit, ExternalLink, Plug, Trash } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/shadcn/badge";
import { Skeleton } from "@/components/shadcn/skeleton";
import { Switch } from "@/components/shadcn/switch";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { NoxusMenu } from "@/components/UI/NoxusMenu";
import { AddNewEntry } from "@/components/Assistant/AssistantDetailsContent";
import {
  MCPServerPickerDialog,
  type MCPPickerCredential,
} from "./MCPServerPickerDialog";
import {
  MCPServerToolsDialog,
  type MCPServerToolsDialogCredential,
} from "./MCPServerToolsDialog";
import { useSpotUser } from "@/utils/spotUser";
import { linkedAccountsUrl } from "@/utils/activeGroup";
import { cn } from "@/lib/utils";
import { useMCPCredentials, type MCPCredentialRow } from "./mcpCredentials";
import { UnavailableResourceBadge } from "@/components/Assistant/UnavailableResourceBadge";

interface MCPServerBinding {
  credential_id: string;
  enabled: boolean;
  selected_tools: string[] | null;
}

function coerceBindings(raw: unknown): MCPServerBinding[] {
  if (!Array.isArray(raw)) return [];
  const out: MCPServerBinding[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      out.push({ credential_id: entry, enabled: true, selected_tools: null });
      continue;
    }
    if (entry && typeof entry === "object") {
      const record = entry as Record<string, unknown>;
      const credential_id = record.credential_id;
      if (typeof credential_id !== "string") continue;
      const selected = record.selected_tools;
      out.push({
        credential_id,
        enabled: record.enabled !== false,
        selected_tools: Array.isArray(selected)
          ? selected.filter((t): t is string => typeof t === "string")
          : null,
      });
    }
  }
  return out;
}

export function MCPCredentialField({
  name,
  control,
  label,
  isDisabled,
}: {
  name: string;
  control: Control<Record<string, unknown>>;
  label: string;
  tooltip?: string | null;
  isDisabled?: boolean;
}) {
  const { spotGroup } = useSpotUser();
  const { credentials, isLoading, catalogLoaded } = useMCPCredentials();
  const byId = useMemo(() => {
    const m = new Map<string, MCPCredentialRow>();
    for (const c of credentials) m.set(c.id, c);
    return m;
  }, [credentials]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingCredId, setEditingCredId] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-sm font-medium text-foreground leading-none">
          {label}
        </p>
        <p className="text-xs font-regular text-muted-foreground mt-1.5">
          Select the MCP servers this agent can access.
        </p>
      </div>

      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const bindings = coerceBindings(field.value);
          const usedIds = new Set(bindings.map((b) => b.credential_id));
          const available = credentials.filter((c) => !usedIds.has(c.id));

          const updateBinding = (
            credId: string,
            patch: Partial<MCPServerBinding>
          ) => {
            const next = bindings.map((b) =>
              b.credential_id === credId ? { ...b, ...patch } : b
            );
            field.onChange(next);
          };

          const removeBinding = (credId: string) => {
            field.onChange(bindings.filter((b) => b.credential_id !== credId));
          };

          const addBinding = (credId: string) => {
            field.onChange([
              ...bindings,
              { credential_id: credId, enabled: true, selected_tools: null },
            ]);
          };

          if (isLoading) {
            return <Skeleton className="h-10 w-full" />;
          }

          if (bindings.length === 0 && credentials.length === 0) {
            return (
              <NoxusAlert
                icon={Plug}
                description="No MCPs available."
                action={{
                  label: (
                    <>
                      <ExternalLink />
                      Open connections
                    </>
                  ),
                  onClick: () =>
                    window.open(
                      linkedAccountsUrl(spotGroup.id),
                      "_blank",
                      "noopener,noreferrer"
                    ),
                }}
              />
            );
          }

          const pickerCredentials: MCPPickerCredential[] = available.map(
            (c) => ({
              id: c.id,
              display_name: c.display_name,
              integration_name: c.integration_name,
              integration_image: c.integration_image,
              enabled_tools_count: c.enabled_tools.length,
            })
          );

          const editingCred = editingCredId ? byId.get(editingCredId) : null;
          const editingBinding = editingCredId
            ? bindings.find((b) => b.credential_id === editingCredId)
            : undefined;
          const editingDialogCred: MCPServerToolsDialogCredential | undefined =
            editingCred
              ? {
                  display_name: editingCred.display_name,
                  integration_name: editingCred.integration_name,
                  integration_image: editingCred.integration_image,
                  discovered_tools: editingCred.discovered_tools,
                  enabled_tools: editingCred.enabled_tools,
                }
              : undefined;

          const canAdd = !isDisabled && available.length > 0;

          return (
            <div className="flex flex-col gap-2">
              {bindings.map((binding) => {
                const cred = byId.get(binding.credential_id);
                return (
                  <MCPServerEntry
                    key={binding.credential_id}
                    binding={binding}
                    credential={cred}
                    isMissing={!cred && catalogLoaded}
                    onToggle={(enabled) =>
                      updateBinding(binding.credential_id, { enabled })
                    }
                    onEdit={() => setEditingCredId(binding.credential_id)}
                    onRemove={() => removeBinding(binding.credential_id)}
                    isDisabled={isDisabled}
                  />
                );
              })}

              <AddNewEntry
                label="Add MCP Server"
                disabledLabel={
                  available.length === 0 ? "No more MCPs to add" : undefined
                }
                onClick={() => setPickerOpen(true)}
                isDisabled={!canAdd}
              />

              <MCPServerPickerDialog
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                available={pickerCredentials}
                onAdd={addBinding}
              />

              <MCPServerToolsDialog
                isOpen={!!editingCredId}
                onClose={() => setEditingCredId(null)}
                credential={editingDialogCred}
                selectedTools={editingBinding?.selected_tools ?? null}
                onSave={(selected_tools) => {
                  if (editingCredId) {
                    updateBinding(editingCredId, { selected_tools });
                  }
                }}
                isDisabled={isDisabled}
              />
            </div>
          );
        }}
      />
    </div>
  );
}

function MCPServerEntry({
  binding,
  credential,
  isMissing,
  onToggle,
  onEdit,
  onRemove,
  isDisabled,
}: {
  binding: MCPServerBinding;
  credential: MCPCredentialRow | undefined;
  isMissing?: boolean;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onRemove: () => void;
  isDisabled?: boolean;
}) {
  const connectionEnabled = useMemo(
    () => new Set(credential?.enabled_tools ?? []),
    [credential?.enabled_tools]
  );
  const availableToolsCount = useMemo(
    () =>
      (credential?.discovered_tools ?? []).filter((t) =>
        connectionEnabled.has(t.name)
      ).length,
    [credential?.discovered_tools, connectionEnabled]
  );

  const selectedCount = useMemo(() => {
    if (binding.selected_tools === null) return availableToolsCount;
    return binding.selected_tools.filter((n) => connectionEnabled.has(n))
      .length;
  }, [binding.selected_tools, connectionEnabled, availableToolsCount]);

  const headerName =
    credential?.display_name ??
    (isMissing ? "Disconnected MCP server" : "MCP server");
  const status = credential?.status;

  return (
    <div className="flex items-center justify-between gap-2 p-2 border border-border rounded-lg w-full">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {credential?.integration_image ? (
          <img
            src={credential.integration_image}
            alt={credential.integration_name}
            className="size-6 rounded-sm shrink-0"
          />
        ) : (
          <Plug className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span
          className={cn(
            "text-sm font-medium truncate",
            isMissing && "text-muted-foreground"
          )}
        >
          {headerName}
        </span>
        {isMissing ? (
          <UnavailableResourceBadge kind="mcp" />
        ) : (
          <>
            {status && status !== "active" && (
              <Badge variant="outline" className="text-xs">
                {status}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground shrink-0">
              {selectedCount}/{availableToolsCount} tools
            </span>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={binding.enabled}
          onCheckedChange={onToggle}
          disabled={isDisabled || isMissing}
        />
        <NoxusMenu
          isSmall
          itemsArray={[
            {
              label: "Edit",
              icon: Edit,
              onClick: onEdit,
              isHidden: !credential || isMissing,
            },
            {
              label: "Remove",
              icon: Trash,
              onClick: onRemove,
              color: "destructive",
            },
          ]}
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
}
